/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

/**
 * ThemeContext (V425) — app-wide light/dark/system theme.
 *
 * - preference: 'light' | 'dark' | 'system' (default 'system')
 * - Applied by toggling the `dark` class on <html> (tailwind darkMode:class)
 * - Instant paint from localStorage ('iev_theme'); synced to the
 *   authenticated user's account (GET/PUT /api/me/theme) so it persists
 *   across logout, login, refresh and devices.
 */
const ThemeContext = createContext(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  return ctx || { preference: 'system', resolved: 'light', setPreference: () => {} };
};

const media = () => window.matchMedia('(prefers-color-scheme: dark)');

const resolve = (pref) => (pref === 'system' ? (media().matches ? 'dark' : 'light') : pref);

const applyToDom = (pref) => {
  const dark = resolve(pref) === 'dark';
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
};

export const ThemeProvider = ({ children }) => {
  const [preference, setPref] = useState(() => {
    const stored = localStorage.getItem('iev_theme');
    return ['light', 'dark', 'system'].includes(stored) ? stored : 'system';
  });
  const [resolved, setResolved] = useState(() => resolve(preference));
  // Race guards: only pull the server pref once per token, and never let
  // an in-flight GET overwrite a preference the user just clicked.
  const fetchedForToken = React.useRef(null);
  const userDirty = React.useRef(false);

  // Apply on every preference change + follow OS changes in system mode.
  useEffect(() => {
    applyToDom(preference);
    setResolved(resolve(preference));
    const m = media();
    const onChange = () => {
      if (preference === 'system') {
        applyToDom('system');
        setResolved(resolve('system'));
      }
    };
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, [preference]);

  // Pull the account-level preference whenever a session token appears
  // (initial load AND after login) so the choice follows the user across
  // devices. localStorage already painted the last-known value, so
  // there's no flash.
  const { token } = useAuth();
  useEffect(() => {
    if (!token || fetchedForToken.current === token) return;
    fetchedForToken.current = token;
    (async () => {
      try {
        const { data } = await api.get('/me/theme');
        if (userDirty.current) return; // user changed it while the GET was in flight
        if (data?.theme && ['light', 'dark', 'system'].includes(data.theme)) {
          localStorage.setItem('iev_theme', data.theme);
          setPref(data.theme);
        }
      } catch {
        // Offline / legacy token — localStorage value stands.
      }
    })();
  }, [token]);

  const setPreference = useCallback((next) => {
    if (!['light', 'dark', 'system'].includes(next)) return;
    userDirty.current = true;
    localStorage.setItem('iev_theme', next);
    setPref(next);
    if (localStorage.getItem('token')) {
      api.put('/me/theme', { theme: next }).catch(() => {});
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, resolved, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
